import { fetchUserChatProfile } from '@/lib/api/users';
import { fetchThread } from '@/lib/api/threads';
import { isWebMode } from '@/lib/config/web-mode';
import { requestOpenThreadMessage } from '@/lib/notifications/notification-navigation';
import { resolveNotificationIcon } from '@/lib/notifications/notification-avatar-icon';
import {
  ensureNotificationPermission,
  getNotificationPermissionState,
  showSystemNotification,
} from '@/lib/notifications/system-notifications';
import { getAuthToken, getAuthUser } from '@/lib/api/token-store';
import { getSelfAvailability } from '@/lib/realtime/presence-service';
import { scopeForThreadMetadata } from '@/lib/notifications/thread-scope';
import {
  isKnownDirectThreadId,
  isKnownInternalThreadId,
  noteDirectThreadIds,
  noteInternalThreadIds,
} from '@/lib/stores/chats-unread-store';
import { isChatMuted } from '@/lib/stores/user-preference-store';
import {
  getWhatsAppPatientPushName,
  getWhatsAppPatientSenderLabel,
  isSenderBotMessage,
  isWhatsAppBridgeStubName,
  isWhatsAppStaffAnswerMessage,
} from '@/lib/messages/messages-helpers';
import {
  isAttentionCallLogMessage,
  isStandaloneSystemLineMessage,
  parseMessageBodyText,
} from '@/lib/realtime/chat-message-helpers';
import { attachmentIsSticker, attachmentMediaPreviewVariant, parseAttachments } from '@/lib/realtime/message-attachments';
import { getUserIdsFromJwt } from '@/lib/realtime/jwt-user-ids';

const PREVIEW_MAX_CHARS = 140;

let messagesRouteActive = false;
let activeThreadIdForNotify = null;

/**
 * Updates whether the Messages page is shown and which thread is open (suppresses duplicate toasts while viewing that thread).
 */
export function setChatNotificationMessagesRouteState({ isActive, selectedThreadId }) {
  messagesRouteActive = Boolean(isActive);
  activeThreadIdForNotify = selectedThreadId ? String(selectedThreadId).trim() : null;
}

/** Whether metadata.mentions targets the current user (an @mention beats a chat mute). */
function messageMentionsUser(message) {
  const metadata = message?.metadata && typeof message.metadata === 'object' ? message.metadata : null;
  const mentions = Array.isArray(metadata?.mentions) ? metadata.mentions : [];
  if (!mentions.length) {
    return false;
  }
  const currentUserId = String(getAuthUser()?.id || '').trim();
  const jwtIds = getUserIdsFromJwt(getAuthToken());
  return mentions.some((raw) => {
    const id = String(raw || '').trim();
    return Boolean(id) && (id === currentUserId || jwtIds.includes(id));
  });
}

function isOwnMessage(message) {
  const senderId = String(message?.sender?.userId || '').trim();
  if (!senderId) {
    return false;
  }
  const currentUserId = String(getAuthUser()?.id || '').trim();
  if (currentUserId && senderId === currentUserId) {
    return true;
  }
  return getUserIdsFromJwt(getAuthToken()).includes(senderId);
}

function shouldSuppressForActiveView(message) {
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
    return false;
  }
  if (!messagesRouteActive) {
    return false;
  }
  const tid = String(message?.threadId || '').trim();
  if (!tid || !activeThreadIdForNotify) {
    return false;
  }
  return tid === activeThreadIdForNotify;
}

// Threads confirmed NOT internal this session, so repeated patient-thread messages never
// re-fetch. The internal side is cached in chats-unread-store's known-id set.
const knownNormalThreadIds = new Set();

/**
 * Which page a clicked notification should open: 'internal' (Chats), 'direct' (Private) or
 * 'normal' (Messages). The chats store's known-id sets answer synchronously for any thread the
 * session has seen; otherwise one thread fetch decides, and every outcome is cached. Errors mean
 * 'normal' — the pre-scope behavior.
 *
 * The 'direct' case is why this cannot just test metadata.internal: a private contact chat carries
 * NEITHER `internal` nor `internalKind` (deliberately — that is what keeps it out of the staff
 * Chats list), so without an explicit branch it fell through to 'normal' and a notification click
 * opened somebody's private conversation inside the shared patient inbox.
 */
async function resolveThreadScopeForNavigation(threadId) {
  const tid = String(threadId || '').trim();
  if (!tid) {
    return 'normal';
  }
  if (isKnownInternalThreadId(tid)) {
    return 'internal';
  }
  if (isKnownDirectThreadId(tid)) {
    return 'direct';
  }
  if (knownNormalThreadIds.has(tid)) {
    return 'normal';
  }
  try {
    const thread = await fetchThread({ threadId: tid });
    const resolved = scopeForThreadMetadata(thread?.metadata);
    if (resolved === 'internal') {
      noteInternalThreadIds([tid]);
      return 'internal';
    }
    if (resolved === 'direct') {
      noteDirectThreadIds([tid]);
      return 'direct';
    }
    knownNormalThreadIds.add(tid);
  } catch (_error) {
    // fall through (uncached — a later attempt may succeed)
  }
  return 'normal';
}

/**
 * Whether the incoming message belongs to an internal chat — needed BEFORE showing the
 * toast (💬 title, always-notify, mute gate). A fresh session's known-id set is cold
 * until Chats opens or the unread summary returns, so unknown ids resolve via one thread
 * fetch — except WhatsApp-marked traffic, which is patient scope by construction (the
 * high-volume path must never fetch per message).
 */
async function resolveIsInternalChatMessage(message, threadId) {
  const tid = String(threadId || '').trim();
  if (!tid) {
    return false;
  }
  if (isKnownInternalThreadId(tid) || isKnownDirectThreadId(tid)) {
    // Private contact chats get the same treatment as staff chats here: a person wrote to you
    // directly, so it is always worth a toast, unlike patient-channel volume.
    return true;
  }
  if (knownNormalThreadIds.has(tid)) {
    return false;
  }
  const metadata = message?.metadata && typeof message.metadata === 'object' ? message.metadata : null;
  const conversationType = String(metadata?.conversationType || '').toLowerCase();
  if (
    // Healo-app patient traffic is marked per-message (top-level channel stamped by the
    // patient app) so, like WhatsApp, it never costs a per-message thread fetch.
    String(message?.channel || '').toLowerCase() === 'app' ||
    (metadata && (metadata.whatsapp || conversationType === 'whatsapp' || conversationType === 'app'))
  ) {
    knownNormalThreadIds.add(tid);
    return false;
  }
  // Both personal scopes get the chat treatment; only the patient inbox does not.
  const resolved = await resolveThreadScopeForNavigation(tid);
  return resolved === 'internal' || resolved === 'direct';
}

function truncatePreview(text) {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (normalized.length <= PREVIEW_MAX_CHARS) {
    return normalized;
  }
  return `${normalized.slice(0, PREVIEW_MAX_CHARS)}…`;
}

function getAttachmentOnlyFallbackBody(message) {
  const attachments = parseAttachments(message).filter((att) => att && typeof att === 'object');
  if (attachments.length === 0) {
    return 'is empty';
  }
  const kinds = new Set(
    attachments.map((att) => {
      if (attachmentIsSticker(att)) return 'sticker';
      const media = attachmentMediaPreviewVariant(att);
      if (media === 'image') return 'image';
      if (media === 'audio') return 'audio';
      if (media === 'video') return 'video';
      return 'attachment';
    }),
  );
  const count = attachments.length;
  if (kinds.size === 1) {
    const kind = [...kinds][0];
    return count === 1 ? `sent ${kind}` : `sent ${kind}s`;
  }
  return count === 1 ? 'sent attachment' : 'sent attachments';
}

/**
 * Resolves the notification's title and icon from the message sender. The icon is the sender's
 * chat-profile avatar — for a 1:1 chat that IS the thread icon (on WhatsApp the bridge syncs the
 * patient's profile photo onto their user), and it falls back to the app icon when absent. `name`
 * is the sender's real name for an initials icon — never the raw sender id or a bridge stub login.
 * @returns {Promise<{ title: string, icon: string, name: string }>}
 */
async function resolveSenderDisplay(message) {
  const sender = message?.sender || {};
  const senderId = String(sender.userId || '').trim();
  const kind = String(sender.kind || 'user').toLowerCase();

  if (!senderId) {
    return { title: 'New message', icon: '', name: '' };
  }
  if (kind === 'bot') {
    return { title: senderId || 'Bot', icon: '', name: '' };
  }
  let profileName = '';
  let icon = '';
  try {
    const profile = await fetchUserChatProfile({ userId: senderId });
    profileName = String(profile?.displayName || '').trim();
    icon = String(profile?.avatar || '').trim();
  } catch (_error) {
    profileName = '';
    icon = '';
  }
  // A WhatsApp number never matched/synced to a real patient resolves to a nameless bridge external-
  // stub login id ("ext-bot-<botId>-<phone>"), not a person. When we have no real patient name like
  // that, title the notification with the patient's WhatsApp identity (name with phone in
  // parentheses, else phone), matching the in-thread sender label. A synced/named patient keeps
  // their real name.
  let title = profileName || senderId;
  let name = profileName;
  if (!profileName || isWhatsAppBridgeStubName(profileName)) {
    const whatsappPatientLabel = getWhatsAppPatientSenderLabel(message);
    if (whatsappPatientLabel) {
      title = whatsappPatientLabel;
    }
    // The label carries the phone in parentheses; the push name alone is the person.
    name = getWhatsAppPatientPushName(message);
  }
  return { title, icon, name };
}

/**
 * Shows a system notification for an incoming chat message (own and system lines are skipped).
 */
export async function notifyIncomingChatMessage(message) {
  if (!message || typeof message !== 'object') {
    return;
  }
  // Web mode can't ask from here (no click), so until this browser has granted notifications
  // nothing below could show — bail before the per-thread scope fetch and profile lookup.
  if (isWebMode() && getNotificationPermissionState() !== 'granted') {
    return;
  }
  // System lines are silent — except a missed/declined call, which is a patient
  // trying to reach the clinic (the body renders as e.g. "Missed voice call").
  if (isStandaloneSystemLineMessage(message) && !isAttentionCallLogMessage(message)) {
    return;
  }
  if (isOwnMessage(message)) {
    return;
  }
  // Bot answers (auto-replies) don't warrant a notification — staff only need to be alerted to
  // patient messages and human replies.
  if (isSenderBotMessage(message)) {
    return;
  }
  // Outbound WhatsApp messages sent from the business number (`metadata.whatsapp.fromMe`) are our
  // own side, not an incoming patient message — staff replies AND the bot's automated confirmations
  // (which the bridge forwards as `kind:"user"`, so the bot check above misses them) both arrive
  // this way. Don't notify for our own outgoing messages.
  if (isWhatsAppStaffAnswerMessage(message)) {
    return;
  }
  // Do-not-disturb mutes ALL desktop notifications for this user until they change status.
  if (getSelfAvailability() === 'dnd') {
    return;
  }
  const threadId = String(message?.threadId || '').trim();
  // Internal chat messages notify UNCONDITIONALLY — even while the user is viewing that
  // exact chat (explicit product decision). Only patient messages keep the active-view
  // suppression (no toast for the conversation you're looking at).
  const isInternalChatMessage = await resolveIsInternalChatMessage(message, threadId);
  if (!isInternalChatMessage && shouldSuppressForActiveView(message)) {
    return;
  }
  // A muted chat stays silent — unless the message @mentions this user (DND above still
  // wins over mentions; mute does not).
  if (isInternalChatMessage && isChatMuted(threadId) && !messageMentionsUser(message)) {
    return;
  }

  const ok = await ensureNotificationPermission();
  if (!ok) {
    return;
  }

  const { title: senderTitle, icon: senderAvatar, name: senderName } = await resolveSenderDisplay(message);
  // Web mode: a browser draws whatever the avatar URL decodes to, so a placeholder photo became a
  // blank tile — keep only a real photo, else the sender's initials (like the in-app <Avatar>).
  const icon = isWebMode()
    ? await resolveNotificationIcon({ avatarUrl: senderAvatar, name: senderName })
    : senderAvatar;
  // Chat notifications look deliberately different from patient-message ones: the 💬
  // prefix is the one cross-platform differentiator (macOS ignores per-notification icons).
  const title = isInternalChatMessage ? `💬 ${senderTitle}` : senderTitle;
  const bodyText = truncatePreview(parseMessageBodyText(message.body));
  const body = bodyText || getAttachmentOnlyFallbackBody(message);

  const messageId = String(message?.id || '').trim();

  showSystemNotification({
    title,
    body,
    // Thread icon: the sender's avatar (patient's WhatsApp photo on bridge threads). Honored on
    // Windows/Linux. macOS IGNORES per-notification icons in Electron — its UNNotification framework
    // always uses the app bundle icon — so this is a deliberate no-op there, not a bug
    // (electron/electron#1025); a custom image on macOS would need a native UNNotificationAttachment.
    // Empty when absent, so platforms that honor it fall back to the app icon (web mode: initials
    // when there is a name, else the Healo logo) rather than a broken image.
    icon,
    // Web mode: a duplicated tab receives the same message on its own socket — one tag per message
    // collapses the copies.
    tag: messageId ? `msg:${messageId}` : '',
    onClick: threadId
      ? () => {
          // Resolve which page owns the thread (Chats vs Messages) at click time, then
          // publish the scoped target — App routes the page, the owning pane opens it.
          void resolveThreadScopeForNavigation(threadId).then((scope) => {
            requestOpenThreadMessage({ threadId, messageId, scope });
          });
        }
      : undefined,
  });
}
